import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type RydSliderInputProps = {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    height?: number; // px
    trackThickness?: number; // px
    thumbSize?: number; // px
    className?: string;
    style?: React.CSSProperties;
    id?: string;
    name?: string; // if provided, a hidden input will be rendered for forms
    formatValue?: (v: number) => string;
    parseValue?: (s: string) => number | null;
    onCommit?: (value: number) => void; // called on drag end or when editing is committed
    ariaLabel?: string;
};

function clamp(v: number, min: number, max: number) {
    return Math.min(max, Math.max(min, v));
}

function roundToStep(v: number, min: number, step: number) {
    if (!isFinite(step) || step <= 0) return v;
    const n = Math.round((v - min) / step);
    return min + n * step;
}

function guessStep(min: number, max: number) {
    const range = Math.abs(max - min) || 1;
    const rough = range / 100;
    // Round rough step to a "nice" value
    const pow10 = Math.pow(10, Math.floor(Math.log10(rough)));
    const candidates = [1, 2, 5, 10].map((m) => m * pow10);
    let best = candidates[0];
    for (const c of candidates) if (Math.abs(c - rough) < Math.abs(best - rough)) best = c;
    return best;
}

function decimalPlaces(step: number) {
    const s = String(step);
    if (s.includes("e-")) {
        const [, exp] = s.split("e-");
        return parseInt(exp, 10);
    }
    const dot = s.indexOf(".");
    return dot >= 0 ? s.length - dot - 1 : 0;
}

const defaultFormat = (v: number, step: number) => v.toFixed(Math.min(6, decimalPlaces(step)));

export default function RydSliderInput(props: RydSliderInputProps) {
    const {
        value,
        onChange,
        min = 0,
        max = 100,
        step: stepProp,
        disabled = false,
        height = 200,
        trackThickness = 6,
        thumbSize = 16,
        className,
        style,
        id,
        name,
        formatValue,
        parseValue,
        onCommit,
        ariaLabel,
    } = props;

    const step = useMemo(() => stepProp ?? guessStep(min, max), [stepProp, min, max]);
    const decimals = useMemo(() => decimalPlaces(step), [step]);

    const safeRange = useMemo(() => {
        const lo = Math.min(min, max);
        const hi = Math.max(min, max);
        return { lo, hi };
    }, [min, max]);

    const vClamped = clamp(value, safeRange.lo, safeRange.hi);

    const trackRef = useRef<HTMLDivElement | null>(null);
    const draggingRef = useRef<null | { pointerId: number }>(null);

    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState("");

    useEffect(() => {
        if (!editing) return;
        setEditText(
            (formatValue ? formatValue(vClamped) : defaultFormat(vClamped, step)).replace(/(\.0+|(?<=\.\d*?)0+)$/, "")
        );
    }, [editing, vClamped, step, formatValue]);

    const toPct = useCallback(
        (v: number) => {
            if (safeRange.hi === safeRange.lo) return 0;
            return (v - safeRange.lo) / (safeRange.hi - safeRange.lo);
        },
        [safeRange.hi, safeRange.lo]
    );

    const pct = clamp(toPct(vClamped), 0, 1);

    const computeValueFromPointer = useCallback(
        (clientY: number) => {
            const el = trackRef.current;
            if (!el) return vClamped;
            const rect = el.getBoundingClientRect();
            const ratio = clamp((clientY - rect.top) / rect.height, 0, 1);
            // Up increases value like a fader: top => max, bottom => min
            const raw = safeRange.hi - ratio * (safeRange.hi - safeRange.lo);
            const rounded = roundToStep(raw, safeRange.lo, step);
            return clamp(rounded, safeRange.lo, safeRange.hi);
        },
        [safeRange.hi, safeRange.lo, step, vClamped]
    );

    const commit = useCallback(
        (v: number) => {
            onCommit?.(v);
        },
        [onCommit]
    );

    const startDrag = useCallback(
        (e: React.PointerEvent) => {
            if (disabled) return;
            const el = trackRef.current;
            if (!el) return;
            (e.target as Element).setPointerCapture?.(e.pointerId);
            draggingRef.current = { pointerId: e.pointerId };
            const next = computeValueFromPointer(e.clientY);
            if (next !== value) onChange(next);
        },
        [computeValueFromPointer, disabled, onChange, value]
    );

    const onPointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (disabled) return;
            if (!draggingRef.current || draggingRef.current.pointerId !== e.pointerId) return;
            const next = computeValueFromPointer(e.clientY);
            if (next !== value) onChange(next);
        },
        [computeValueFromPointer, disabled, onChange, value]
    );

    const endDrag = useCallback(
        (e: React.PointerEvent) => {
            if (!draggingRef.current || draggingRef.current.pointerId !== e.pointerId) return;
            draggingRef.current = null;
            commit(value);
        },
        [commit, value]
    );

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (disabled) return;
            let next = value;
            const big = step * 10;
            let handled = true;
            switch (e.key) {
                case "ArrowUp":
                    next = value + step;
                    break;
                case "ArrowDown":
                    next = value - step;
                    break;
                case "PageUp":
                    next = value + big;
                    break;
                case "PageDown":
                    next = value - big;
                    break;
                case "Home":
                    next = safeRange.lo;
                    break;
                case "End":
                    next = safeRange.hi;
                    break;
                default:
                    handled = false;
            }
            if (handled) {
                e.preventDefault();
                next = roundToStep(next, safeRange.lo, step);
                next = clamp(next, safeRange.lo, safeRange.hi);
                if (next !== value) onChange(next);
                commit(next);
            }
        },
        [disabled, value, step, safeRange.lo, safeRange.hi, onChange, commit]
    );

    const startEditing = useCallback(() => {
        if (disabled) return;
        setEditing(true);
    }, [disabled]);

    const parseInput = useCallback(
        (s: string) => {
            if (parseValue) {
                const r = parseValue(s);
                if (r == null || !isFinite(r)) return null;
                return r;
            }
            const n = Number(String(s).replace(",", "."));
            if (!isFinite(n)) return null;
            return n;
        },
        [parseValue]
    );

    const finishEditing = useCallback(
        (commitChange: boolean) => {
            if (!editing) return;
            setEditing(false);
            if (!commitChange) return;
            const parsed = parseInput(editText);
            if (parsed == null) return;
            let next = roundToStep(parsed, safeRange.lo, step);
            next = clamp(next, safeRange.lo, safeRange.hi);
            if (next !== value) onChange(next);
            commit(next);
        },
        [editing, editText, parseInput, safeRange.lo, safeRange.hi, step, value, onChange, commit]
    );

    const handleWheel = useCallback(
        (e: React.WheelEvent) => {
            if (disabled) return;
            if (!e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) return; // require a modifier to avoid hijacking scroll
            e.preventDefault();
            const delta = e.deltaY > 0 ? -step : step;
            let next = clamp(roundToStep(value + delta, safeRange.lo, step), safeRange.lo, safeRange.hi);
            if (next !== value) {
                onChange(next);
                commit(next);
            }
        },
        [disabled, value, step, safeRange.lo, safeRange.hi, onChange, commit]
    );

    // Styles
    const WIDTH = Math.max(thumbSize, trackThickness + 10);
    const TRACK_RADIUS = trackThickness / 2;

    const containerStyle: React.CSSProperties = {
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        userSelect: "none",
        opacity: disabled ? 0.6 : 1,
        ...style,
    };

    const valueBoxStyle: React.CSSProperties = {
        marginBottom: 8,
        minWidth: Math.max(40, WIDTH),
        padding: "4px 6px",
        fontSize: 12,
        lineHeight: "1.2",
        textAlign: "center",
        borderRadius: 4,
        border: "1px solid #d0d7de",
        background: "#fff",
        color: "#24292f",
        cursor: disabled ? "not-allowed" : "text",
    };

    const sliderWrapperStyle: React.CSSProperties = {
        position: "relative",
        width: WIDTH,
        height,
        padding: `${thumbSize / 2}px 0`,
        boxSizing: "content-box",
        touchAction: "none",
    };

    const trackStyle: React.CSSProperties = {
        position: "absolute",
        left: "50%",
        top: 0,
        transform: "translateX(-50%)",
        width: trackThickness,
        height: "100%",
        background: "linear-gradient(#e7ebf0, #dfe3e8)",
        borderRadius: TRACK_RADIUS,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)",
    };

    const fillStyle: React.CSSProperties = {
        position: "absolute",
        left: "50%",
        bottom: 0,
        transform: "translateX(-50%)",
        width: trackThickness,
        height: `${pct * 100}%`,
        background: "linear-gradient(#4f9ef8, #1f6feb)",
        borderRadius: TRACK_RADIUS,
    };

    const thumbStyle: React.CSSProperties = {
        position: "absolute",
        left: "50%",
        transform: `translate(-50%, ${(-thumbSize / 2) + (1 - pct) * (height)}px)`,
        width: Math.max(thumbSize, trackThickness + 8),
        height: thumbSize,
        borderRadius: 3,
        background: "linear-gradient(#f6f8fa, #eaeef2)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.2), inset 0 0 0 1px rgba(0,0,0,0.1)",
        cursor: disabled ? "not-allowed" : "grab",
    };

    const sliderA11yProps = {
        role: "slider",
        "aria-valuemin": safeRange.lo,
        "aria-valuemax": safeRange.hi,
        "aria-valuenow": Number(vClamped.toFixed(decimals)),
        "aria-disabled": disabled || undefined,
        "aria-label": ariaLabel,
        tabIndex: disabled ? -1 : 0,
    } as const;

    return (
        <div id={id} className={className} style={containerStyle} onWheel={handleWheel}>
            <div
                style={valueBoxStyle}
                onClick={startEditing}
                title={disabled ? undefined : "Click to edit"}
            >
                {editing ? (
                    <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={() => finishEditing(true)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                finishEditing(true);
                            } else if (e.key === "Escape") {
                                e.preventDefault();
                                finishEditing(false);
                            }
                        }}
                        autoFocus
                        style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            font: "inherit",
                            color: "inherit",
                            background: "transparent",
                            textAlign: "center",
                        }}
                        inputMode="decimal"
                    />
                ) : (
                    <span style={{ cursor: disabled ? "not-allowed" : "text", whiteSpace: "nowrap" }}>
                        {(formatValue ? formatValue(vClamped) : defaultFormat(vClamped, step)).replace(
                            /(\.0+|(?<=\.\d*?)0+)$/,
                            ""
                        )}
                    </span>
                )}
            </div>

            <div
                ref={trackRef}
                style={sliderWrapperStyle}
                onPointerDown={startDrag}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={onKeyDown}
                {...sliderA11yProps}
            >
                <div style={trackStyle} />
                <div style={fillStyle} />
                <div style={thumbStyle} />
            </div>

            {name ? <input type="hidden" name={name} value={String(vClamped)} /> : null}
        </div>
    );
}